// src/lib/theme.ts
import { createTheme, type Theme } from '@mui/material/styles';
import {
  type ColorTheme,
  type ColorMode,
  themesByMode,
  withOpacity,
} from './colors';

// Make our ColorTheme available on the MUI theme so styled() callbacks can
// access it reactively via ({ theme }) => theme.colors.x.y. The MUI theme
// itself is rebuilt by ColorModeProvider whenever the mode flips.
declare module '@mui/material/styles' {
  interface Theme {
    colors: ColorTheme;
  }
  interface ThemeOptions {
    colors?: ColorTheme;
  }

  // Keep legacy custom palette entries available (some code may still
  // reference theme.palette.dalGold / dalBlack).
  interface Palette {
    dalGold:  Palette['primary'];
    dalBlack: Palette['primary'];
  }
  interface PaletteOptions {
    dalGold?:  PaletteOptions['primary'];
    dalBlack?: PaletteOptions['primary'];
  }
}

const boxShadow = (color: string, hover = false) =>
  hover ? `0 6px 20px 0 ${color}` : `0 4px 14px 0 ${color}`;

// Build a fully-configured MUI theme for the given mode. Called once per
// mode change by ColorModeProvider; the result is memoized there.
export function buildTheme(mode: ColorMode): Theme {
  const colors = themesByMode[mode];

  return createTheme({
    colors,
    palette: {
      mode,
      primary:   colors.primary,
      secondary: colors.secondary,
      background: {
        default: colors.background.default,
        paper:   colors.background.paper,
      },
      text: {
        primary:   colors.text.primary,
        secondary: colors.text.secondary,
      },
      divider: colors.border.primary,
      dalGold:  colors.brand.accent,
      dalBlack: colors.brand.neutral,
    },
    typography: {
      fontFamily: [
        'var(--font-public-sans)',
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 700, fontSize: '3.5rem', lineHeight: 1.2, '@media (max-width:768px)': { fontSize: '2.5rem' } },
      h2: { fontWeight: 600, fontSize: '2.5rem', lineHeight: 1.3, '@media (max-width:768px)': { fontSize: '2rem'   } },
      h3: { fontWeight: 600, fontSize: '2rem',   lineHeight: 1.3, '@media (max-width:768px)': { fontSize: '1.8rem' } },
      h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
      h6: { fontWeight: 600, fontSize: '1rem',   lineHeight: 1.4 },
      body1:  { fontSize: '1rem',     lineHeight: 1.6 },
      body2:  { fontSize: '0.875rem', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', fontSize: '1rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background.default,
            color: colors.text.primary,
          },
          '.dalcon-shape': { borderRadius: '50px 10px' },
          html: { scrollBehavior: 'smooth' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '10px 24px', fontSize: '1rem', fontWeight: 600 },
          contained: {
            boxShadow: boxShadow(colors.effects.shadow),
            '&:hover': { boxShadow: boxShadow(colors.effects.shadow, true) },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover':  { borderWidth: '2px' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${colors.border.secondary}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 25px 0 ${colors.effects.backdrop}`,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 500 },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 24,
            paddingRight: 24,
            '@media (max-width:768px)': { paddingLeft: 16, paddingRight: 16 },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: withOpacity(colors.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${withOpacity(colors.border.accent, 0.1)}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.paper,
            borderRight: `1px solid ${withOpacity(colors.border.accent, 0.1)}`,
          },
        },
      },
    },
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
  });
}
